/**
 * CAMPO DI UN PARAMETRO DICHIARATO — il form generico guidato dal `type` di un
 * {@link AutomationParam}.
 *
 * Condiviso fra il blocco QUANDO (parametri dell'evento, in `AutomationForm`) e la modale delle
 * azioni (parametri che un'azione dichiara, in `DeclaredParamsActionConfig`): stesso descrittore,
 * stesso widget. Il componente non sa dove finisca il valore — lo riceve e lo restituisce grezzo
 * (stringa mentre si digita, booleano per lo Switch); a tipizzarlo è chi lo ospita, al boundary.
 */

import { useTranslation } from 'react-i18next';
import Switch from '@mui/material/Switch';
import { ChipSelect } from './catalogFieldEditors';
import type { AutomationParam } from './automationTypes';

/**
 * Un campo per ogni {@link AutomationParam}, scelto sul `type` (SPI ParamType). Il flag `range`
 * sdoppia il campo in from/to (solo tipi rangeable). Il valore è scalare per i param semplici,
 * `{ from, to }` per i range; nel QUANDO risiede in `eventConfig[param.name]`.
 */
export default function ParamField({
  param,
  value,
  onChange,
}: {
  param: AutomationParam;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const { t } = useTranslation();

  // BOOLEAN: uno Switch, nessuna forma a intervallo.
  if (param.type === 'BOOLEAN') {
    return (
      <span className="builder-fieldnum">
        {param.label}
        <Switch
          size="small"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      </span>
    );
  }

  // ENUM: tendina dei valori che l'ENTITÀ dichiara — «stato modificato a» offre gli stati del
  // preventivo o quelli dell'anagrafica a seconda di dove l'evento sia dichiarato, e il form non
  // sa (né deve sapere) di quale campo si tratti. Stesso ChipSelect del blocco SE perché è lo
  // stesso vocabolario: un valore di stato si sceglie allo stesso modo che lo si filtri o che lo si
  // attenda. Nessuna forma a intervallo: fra due valori enumerati non c'è un "in mezzo".
  if (param.type === 'ENUM') {
    return (
      <span className="builder-fieldnum">
        {param.label}{' '}
        <ChipSelect
          className="builder-selchip builder-selchip--val"
          value={(value ?? '') as string}
          options={param.enumValues.map((ev) => ({ value: ev.value, label: ev.label }))}
          placeholder={t('automation.form.builder.selectValue')}
          ariaLabel={param.label}
          onChange={onChange}
        />
      </span>
    );
  }

  // Tipo scalare → input HTML: number (con step) per i numerici, date per DATE, text per STRING.
  const inputType = param.type === 'DATE' ? 'date' : param.type === 'STRING' ? 'text' : 'number';
  const step =
    param.type === 'INTEGER' ? '1' : param.type === 'DECIMAL' || param.type === 'MONEY' ? '0.01' : undefined;
  const suffix = param.type === 'MONEY' ? ' €' : '';

  // Larghezza per-tipo: il CSS di `.builder-fieldnum input` è tarato su INTEGER (giorni). DATE
  // (datepicker nativo) e MONEY/DECIMAL (decimali + suffisso) hanno bisogno di più spazio, e non
  // sono distinguibili via CSS (MONEY/DECIMAL sono `type="number"` come INTEGER) → larghezza inline.
  const inputWidth =
    param.type === 'DATE' ? 100 : param.type === 'MONEY' || param.type === 'DECIMAL' ? 96
      : param.type === 'STRING' ? 180 : 56;

  const renderInput = (v: string, set: (s: string) => void, aria: string) => (
    <input
      type={inputType}
      step={step}
      value={v}
      aria-label={aria}
      style={{ width: inputWidth }}
      onChange={(e) => set(e.target.value)}
    />
  );

  // Range: due input from/to, valore { from, to }.
  if (param.range) {
    const rv = (value ?? {}) as { from?: string; to?: string };
    const fromLabel = t('automation.form.builder.rangeFrom');
    const toLabel = t('automation.form.builder.rangeTo');
    return (
      <span className="builder-fieldnum">
        {param.label} {fromLabel}{' '}
        {renderInput(rv.from ?? '', (s) => onChange({ ...rv, from: s }), `${param.label} ${fromLabel}`)} {toLabel}{' '}
        {renderInput(rv.to ?? '', (s) => onChange({ ...rv, to: s }), `${param.label} ${toLabel}`)}
        {suffix}
      </span>
    );
  }

  return (
    <span className="builder-fieldnum">
      {param.label} {renderInput((value ?? '') as string, (s) => onChange(s), param.label)}
      {suffix}
    </span>
  );
}
